/**
 * houseHistory — PROPERTY-LINK-01 (Seth 2026-10-16, 300 Lake St W: Landschute
 * 2024 → Gale 2026; "Miles can't see any history from 2024").
 *
 * The HOUSE's other ESC accounts: a builder before the homeowner, a previous
 * owner, a landlord beside a tenant. ESC keeps one customer per party, so one
 * house can carry two or three accounts and each one sees only its own visits
 * and units. This module resolves the others from `property_history_at`
 * (address match: house number + zip + first street word + unit) — the same
 * derived link the 360's Property Timeline has trusted since June — and names
 * the relationship by date: the earlier account's last visit before this
 * account's first = "before", the reverse = "after", anything else = overlap.
 * When the seeded property link lands (slice A) this reads the recorded link
 * first and falls back to the address match.
 *
 * Nothing merges and nothing moves: the other account's invoices stay its
 * invoices — surfaces that use this show WHAT WAS DONE and mute the money.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { EquipmentRow } from "@/lib/equipment";

/** Same company test the 360's Property Timeline uses to say "builder / company". */
export const COMPANY_RE =
  /\b(LLC|INC|HOMES?|CONSTRUCTION|BUILDERS?|BUILDING|DEVELOP|PROPERTIES|MGMT|MANAGEMENT|APARTMENTS?|REALTY|RENTALS?|GROUP|CORP|COMPANY|COMPANIES|PARTNERS|TRUST|ASSOC|CHURCH|SCHOOL|CITY OF|INVESTMENTS?|ENTERPRISES?|VENTURES?|HOLDINGS?|CONTRACTING|REMODEL(?:ING|ERS)?|DESIGNS?)\b/i;

/** The surname ESC files a person under ("Gale,Kelley & Laurie" → "gale"; a company → its first word). */
export function surnameKey(fullname: string | null | undefined): string {
  const f = (fullname ?? "").replace(/\s+\d{4,}\s*$/, "").trim();
  const head = f.includes(",") ? f.split(",")[0] : f.split(/\s+/).pop() ?? f;
  return head.toLowerCase().replace(/[^a-z]/g, "");
}

export type HouseRelation = "self" | "before" | "after" | "overlap" | "none";

export interface HouseAccount {
  custno: string;
  locno: string | null;
  /** "The Landschute Group, Inc." · "Jeff Linder" */
  name: string;
  isSelf: boolean;
  isCompany: boolean;
  firstService: string | null;
  lastService: string | null;
  visitCount: number;
  relation: HouseRelation;
  /** "builder / company" · "previous owner" · "later owner" · "also at this address" */
  role: string;
  /** "2023–24" · "2021" · "" */
  years: string;
  /** recorded = a human confirmed the link (pegasus_property_ownership) · derived = the address match */
  source: "recorded" | "derived";
  /** The recorded tenure, when there is one: owner | builder | tenant and its dates. */
  recordedRole: string | null;
  startDate: string | null;
  endDate: string | null;
  propertyId: string | null;
}

interface Row {
  custno: string; locno: string | null; fullname: string | null; locname: string | null;
  first_service: string | null; last_service: string | null; visit_count: number; is_self: boolean;
  source: "recorded" | "derived"; role: string | null; start_date: string | null; end_date: string | null; property_id: string | null;
}

/** ESC's "Last,First & Spouse 0001005" → "First & Spouse Last"; a named location wins. */
const CORP_SUFFIX_RE = /^(inc|llc|llp|lp|ltd|co|corp|pllc|pc|pa)\.?$/i;
export function cleanPartyName(fullname: string | null, locname: string | null): string {
  if (locname && !/^\d/.test(locname.trim())) return locname.trim();
  const f = (fullname ?? "").replace(/\s+\d{4,}\s*$/, "").trim();
  const m = f.match(/^([^,]+),(.+)$/);
  if (!m) return f;
  // "The Landschute Group, Inc." is a company, not "Inc. The Landschute Group".
  if (CORP_SUFFIX_RE.test(m[2].trim())) return f;
  return `${m[2].trim()} ${m[1].trim()}`.trim();
}

const yr = (d: string | null) => (d ? new Date(d).getFullYear() : null);
export function yearsLabel(first: string | null, last: string | null): string {
  const a = yr(first); const b = yr(last);
  if (!a && !b) return "";
  if (!a || !b || a === b) return String(a ?? b);
  return `${a}–${String(b).slice(2)}`;
}

function relate(self: Row | undefined, other: Row): HouseRelation {
  // A recorded tenure dates the handoff itself; visits only stand in when there is none.
  if (other.source === "recorded" && self?.source === "recorded") {
    if (other.end_date && self.start_date && other.end_date <= self.start_date) return "before";
    if (other.start_date && self.end_date && other.start_date >= self.end_date) return "after";
    if (other.end_date || self.end_date) return other.end_date ? "before" : "after";
    return "overlap";
  }
  if (other.visit_count === 0 || !other.first_service) return "none";
  const sf = self?.first_service ?? null;
  const sl = self?.last_service ?? null;
  if (!sf) return "before"; // this account has no visits yet — anything on record came before it
  if (other.last_service && other.last_service < sf) return "before";
  if (sl && other.first_service > sl) return "after";
  return "overlap";
}

export function roleLabel(isCompany: boolean, relation: HouseRelation, recordedRole?: string | null): string {
  if (recordedRole === "builder") return "builder";
  if (recordedRole === "tenant") return "tenant";
  if (relation === "overlap") return "also at this address";
  if (isCompany && recordedRole !== "owner") return "builder / company";
  if (relation === "after") return "later owner";
  return "previous owner";
}

/** The accounts that share this account's service address: this one first,
 *  then the others, most recently served first. Empty until the address is known. */
export function useHouseAccounts(custno: string | null | undefined, locno: string | null | undefined) {
  return useQuery({
    queryKey: ["house_accounts", custno ?? null, locno ?? null],
    enabled: !!custno,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<HouseAccount[]> => {
      const { data, error } = await supabase.rpc("house_accounts_at", { p_custno: custno as string, p_locno: locno ?? null });
      if (error) throw error;
      const rows = (data ?? []) as Row[];
      const self = rows.find((r) => r.is_self);
      const out: HouseAccount[] = rows.map((r) => {
        const isCompany = r.role === "builder" || COMPANY_RE.test(r.fullname ?? "") || COMPANY_RE.test(r.locname ?? "");
        const relation: HouseRelation = r.is_self ? "self" : relate(self, r);
        // a recorded tenure dates the era; visits only when nothing was recorded
        const years = r.source === "recorded" && (r.start_date || r.end_date)
          ? yearsLabel(r.start_date ?? r.first_service, r.end_date ?? (r.is_self ? null : r.last_service))
          : yearsLabel(r.first_service, r.last_service);
        return {
          custno: r.custno,
          locno: r.locno,
          name: cleanPartyName(r.fullname, r.locname) || `#${r.custno}`,
          isSelf: r.is_self,
          isCompany,
          firstService: r.first_service,
          lastService: r.last_service,
          visitCount: Number(r.visit_count ?? 0),
          relation,
          role: r.is_self ? "this account" : roleLabel(isCompany, relation, r.role),
          years,
          source: r.source ?? "derived",
          recordedRole: r.role ?? null,
          startDate: r.start_date ?? null,
          endDate: r.end_date ?? null,
          propertyId: r.property_id ?? null,
        };
      });
      return out.sort((a, b) => Number(b.isSelf) - Number(a.isSelf) || (b.lastService ?? "").localeCompare(a.lastService ?? ""));
    },
  });
}

/** "The Landschute Group, Inc. · builder / company · 2023–24" */
export function houseEraLabel(a: HouseAccount): string {
  return [a.name, a.role, a.years].filter(Boolean).join(" · ");
}

/** The word in front of an era divider, relative to the account being viewed. */
export function relationWord(r: HouseRelation): string {
  return r === "before" ? "Before this owner" : r === "after" ? "After this owner" : r === "overlap" ? "Also here" : "";
}

/* ── the office's answer ──────────────────────────────────────────────────
   "Same house changing hands?" — one tap records the handoff (a property +
   both tenures, dated at the day this account appeared); "Different unit"
   and "Same person twice" write an unlink so the address match stops pairing
   them (the duplicate pair is the merge tool's business). ────────────────── */
export type HouseAnswer = "same_house" | "different_unit" | "duplicate";

export function useHouseAnswer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; locno: string | null; otherCustno: string; otherLocno: string | null; answer: HouseAnswer; note?: string | null }) => {
      const { data, error } = await supabase.rpc("house_link_answer", {
        p_custno: v.custno, p_locno: v.locno, p_other_custno: v.otherCustno, p_other_locno: v.otherLocno, p_answer: v.answer, p_note: v.note ?? null,
      });
      if (error) throw error;
      return data as { ok: boolean; answer: string; property_id?: string; boundary?: string; overlap?: boolean };
    },
    onSuccess: () => {
      for (const k of ["house_accounts", "house_review", "house_equipment", "m_house_visits", "location_ownership", "property_history", "admin_properties", "customer_metrics", "timeline_extras"]) {
        qc.invalidateQueries({ queryKey: [k] });
      }
    },
  });
}

/** HOUSE-UNDO-01: take a recorded link back. The other party's tenure is kept but
 *  marked unlinked (nothing deleted), the house reads stop counting it, and the
 *  pair won't be suggested again. Linking the same pair again re-activates it. */
export function useHouseUndo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { custno: string; locno: string | null; otherCustno: string; note?: string | null }) => {
      const { data, error } = await supabase.rpc("house_link_undo", { p_custno: v.custno, p_locno: v.locno, p_other_custno: v.otherCustno, p_note: v.note ?? null });
      if (error) throw error;
      return data as { ok: boolean; reason?: string };
    },
    onSuccess: () => {
      for (const k of ["house_accounts", "house_review", "house_equipment", "m_house_visits", "location_ownership", "property_history", "admin_properties", "customer_metrics", "timeline_extras"]) {
        qc.invalidateQueries({ queryKey: [k] });
      }
    },
  });
}

/* ── HOUSE-TICKET-CHECK ────────────────────────────────────────────────────
   The ticket said "2024 — no leaks found"; the house record said two leaks
   were found and repaired. One model read (cached per dispatch + content)
   compares what the office wrote with what the house's prior visits say,
   and only speaks when they disagree. ─────────────────────────────────── */
export interface TicketCheckVisit { date: string | null; tech: string | null; account: string | null; call: string | null; did: string | null }
export interface TicketVerdict { conflict: boolean; headline: string; detail: string; source: string }

export function useHouseTicketCheck(v: { dispatch: string | null; custno: string | null; locno: string | null; note: string | null; visits: TicketCheckVisit[]; ready: boolean }) {
  const key = v.visits.map((x) => `${x.date ?? ""}|${(x.did ?? x.call ?? "").length}`).join(",");
  return useQuery({
    queryKey: ["house_ticket_check", v.dispatch, (v.note ?? "").length, key],
    enabled: v.ready && !!v.dispatch && !!(v.note ?? "").trim() && v.visits.length > 0,
    staleTime: 60 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<TicketVerdict> => {
      const { data, error } = await supabase.functions.invoke("house-ticket-check", {
        body: { dispatch: v.dispatch, custno: v.custno, locno: v.locno, note: v.note, visits: v.visits.slice(0, 8) },
      });
      if (error) return { conflict: false, headline: "", detail: "", source: "error" };
      const d = (data ?? {}) as Partial<TicketVerdict>;
      return { conflict: !!d.conflict, headline: d.headline ?? "", detail: d.detail ?? "", source: d.source ?? "unknown" };
    },
  });
}

/** One address collision the office hasn't answered: this account (the newer
 *  side) and the account with history at the same address. */
export interface HouseReviewRow {
  custno: string; locno: string | null; name: string; address: string | null; city: string | null;
  appeared: string | null; self_visits: number;
  other_custno: string; other_locno: string | null; other_name: string; other_is_company: boolean;
  other_first: string | null; other_last: string | null; other_visits: number;
  relation: "before" | "overlap";
  /** HOUSE-COMPARE-01 (34i98): the OTHER account's address as ESC has it — shown side by side,
   *  so "1689 Oakbrooke Court" vs "1689 Oakbrooke Way" is visible without opening both. */
  other_address?: string | null; other_city?: string | null;
}

/** days = null → everything unanswered (Settings → Addresses); 45 → the morning page. */
export function useHouseReviewList(days: number | null, limit = 200) {
  return useQuery({
    queryKey: ["house_review", days, limit],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<HouseReviewRow[]> => {
      const { data, error } = await supabase.rpc("house_review_list_v2", { p_days: days, p_limit: limit });
      if (error) throw error;
      return ((data ?? []) as HouseReviewRow[]).map((r) => ({
        ...r,
        name: cleanPartyName(r.name, null) || r.name,
        other_name: cleanPartyName(r.other_name, null) || r.other_name,
        self_visits: Number(r.self_visits ?? 0),
        other_visits: Number(r.other_visits ?? 0),
      }));
    },
  });
}

/** "builder paid" · "previous owner paid" — the money line on another account's visit. */
export function paidByLabel(a: HouseAccount): string {
  if (a.relation === "overlap") return `billed to ${a.name}`;
  return a.isCompany ? "builder paid" : "previous owner paid";
}

/* ── equipment the house's other accounts carry ──────────────────────────
   The furnace Sedgwick installed for the builder is still the furnace in the
   basement; ESC keeps it under the builder's account. 131 of the 201 handoffs
   in the census have units under the OLD account. Read-only here: the mirror
   is ESC's (DEC-22), and field corrections stay with the account that owns
   the record. ─────────────────────────────────────────────────────────── */
const EQUIP_SELECT =
  "custno, locno, counter, mfg, model, serial, eq_type, equip_loc, install_date, warranty_date, ser_agr_no, notes, custom_1, custom_2, invoice";

export interface HouseEquipmentBundle { account: HouseAccount; rows: EquipmentRow[] }

/** Units on file under the OTHER accounts at this address, one bundle per account. */
export function useHouseEquipment(others: HouseAccount[]) {
  const key = others.map((a) => `${a.custno}/${a.locno ?? ""}`).join(",");
  return useQuery({
    queryKey: ["house_equipment", key],
    enabled: others.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<HouseEquipmentBundle[]> => {
      const ors = others.map((a) => (a.locno ? `and(custno.eq.${a.custno},locno.eq.${a.locno})` : `custno.eq.${a.custno}`));
      const { data, error } = await supabase.from("mirror_equipment").select(EQUIP_SELECT).or(ors.join(",")).limit(400);
      if (error) throw error;
      const rows = (data ?? []) as Array<EquipmentRow & { custno: string; locno: string | null }>;
      return others
        .map((account) => ({ account, rows: rows.filter((r) => r.custno === account.custno && (!account.locno || r.locno === account.locno)) }))
        .filter((b) => b.rows.length > 0);
    },
  });
}

/* ── HOUSE-COMPARE-01 (34i98) ─────────────────────────────────────────────── */

const DIRS = new Set(["N", "S", "E", "W", "NE", "NW", "SE", "SW", "NORTH", "SOUTH", "EAST", "WEST"]);
const SUFFIX_CANON: Record<string, string> = {
  AVE: "Ave", AV: "Ave", AVENUE: "Ave", BLVD: "Blvd", BOULEVARD: "Blvd", CIR: "Cir", CIRCLE: "Cir", CT: "Ct", COURT: "Ct",
  DR: "Dr", DRIVE: "Dr", LN: "Ln", LANE: "Ln", PKWY: "Pkwy", PARKWAY: "Pkwy", PL: "Pl", PLACE: "Pl", RD: "Rd", ROAD: "Rd",
  ST: "St", STREET: "St", TER: "Ter", TERR: "Ter", TERRACE: "Ter", TRL: "Trl", TRAIL: "Trl", WAY: "Way", WY: "Way",
  CV: "Cv", COVE: "Cv", PT: "Pt", POINT: "Pt", RDG: "Rdg", RIDGE: "Rdg", HWY: "Hwy", HIGHWAY: "Hwy", LOOP: "Loop", PATH: "Path",
  BAY: "Bay", BND: "Bend", BEND: "Bend", CRK: "Crk", CREEK: "Crk", HL: "Hill", HILL: "Hill", HLS: "Hills", HILLS: "Hills",
  LK: "Lake", LAKE: "Lake", SQ: "Sq", SQUARE: "Sq", XING: "Xing", CROSSING: "Xing", PASS: "Pass", RUN: "Run", ROW: "Row", WALK: "Walk",
};
/** Street words after the house number and before any unit, directions off both ends. */
function streetWords(add1: string | null | undefined): string[] {
  const s = (add1 ?? "").replace(/^\s*\d+[A-Za-z]?\s*/, "").replace(/(?:#\s*|\b(?:APT|UNIT|STE|SUITE|RM)\.?\s+)[A-Za-z0-9-]+.*$/i, "");
  const w = s.toUpperCase().replace(/[^A-Z0-9 ]/g, " ").trim().split(/\s+/).filter(Boolean);
  if (w.length > 1 && DIRS.has(w[0])) w.shift();
  if (w.length > 1 && DIRS.has(w[w.length - 1])) w.pop();
  return w;
}
/** The canonical street type of an address ("Ct", "Way"), or null. */
export function streetTypeOf(add1: string | null | undefined): string | null {
  const w = streetWords(add1);
  for (let i = w.length - 1; i >= 1; i--) if (SUFFIX_CANON[w[i]]) return SUFFIX_CANON[w[i]];
  return null;
}
/** Do the two addresses read as the same street? (house number aside — the pair already shares it) */
export function sameStreet(a: string | null | undefined, b: string | null | undefined): boolean {
  const ta = streetTypeOf(a), tb = streetTypeOf(b);
  if (ta && tb && ta !== tb) return false;
  const wa = streetWords(a).filter((x) => !SUFFIX_CANON[x]).join(" ");
  const wb = streetWords(b).filter((x) => !SUFFIX_CANON[x]).join(" ");
  return wa === wb || wa.startsWith(wb + " ") || wb.startsWith(wa + " ");
}

export interface HousePairSide {
  custno: string; locno: string | null;
  phone: string | null; email: string | null; equipment: number;
}
/** The two sides' phone, email and equipment count — what the compare shows beyond the row itself. */
export function useHousePairFacts(row: HouseReviewRow | null) {
  const a = row ? `${row.custno}/${row.locno ?? ""}` : null;
  const b = row ? `${row.other_custno}/${row.other_locno ?? ""}` : null;
  return useQuery({
    queryKey: ["house_pair_facts", a, b],
    enabled: !!row,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ self: HousePairSide; other: HousePairSide }> => {
      const r = row as HouseReviewRow;
      const custnos = [r.custno, r.other_custno];
      const [{ data: locs }, { data: eq }] = await Promise.all([
        supabase.from("mirror_location").select("custno, locno, phone1, phone2, email").in("custno", custnos),
        supabase.from("mirror_equipment").select("custno, locno").in("custno", custnos),
      ]);
      const side = (custno: string, locno: string | null): HousePairSide => {
        const ls = ((locs ?? []) as { custno: string; locno: string; phone1: string | null; phone2: string | null; email: string | null }[]).filter((l) => l.custno === custno);
        const l = ls.find((x) => x.locno === (locno ?? x.locno)) ?? ls[0] ?? null;
        const n = ((eq ?? []) as { custno: string; locno: string | null }[]).filter((e) => e.custno === custno && (!locno || !e.locno || e.locno === locno)).length;
        return { custno, locno, phone: l?.phone1 ?? l?.phone2 ?? null, email: l?.email ?? null, equipment: n };
      };
      return { self: side(r.custno, r.locno), other: side(r.other_custno, r.other_locno) };
    },
  });
}
